import React, { useState, useEffect } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Avatar,
  MenuItem,
  Box,
  TablePagination,
  IconButton,
  Menu
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import SettingsIcon from '@mui/icons-material/Settings'
import { useTheme } from '@mui/material/styles'
import PermissionDialog from './PermissionDialog'
import toast from 'react-hot-toast'
import DeleteMemberOrCancelRequest from './DeleteMeberOrCancelRequestDialog'

const TeamMembersTable = ({ fetchTeamMembers, teamMembers, handleRemoveMemberClick }) => {
  const theme = useTheme()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)
  const [members, setMembers] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const [currentMember, setCurrentMember] = useState(null)
  const [openManagePermissionsDialog, setOpenManagePermissionsDialog] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleMenuOpen = (event, member) => {
    setAnchorEl(event.currentTarget)
    setCurrentMember(member)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleOpenManagePermissionsDialog = () => {
    setOpenManagePermissionsDialog(true)
    handleMenuClose()
  }

  const handleCloseManagePermissionsDialog = () => {
    setOpenManagePermissionsDialog(false)
  }

  const removeMember = () => {
    setOpenDeleteDialog(true)
  }

  const handleConfirmDelete = async () => {
    const payload = {
      id:currentMember?._id,
      userId:currentMember?.userId
    }

    const callback = (data,error) => {

      setOpenDeleteDialog(false)
      handleMenuClose()

      if (data && data.message) {
        toast.success( data.message)
      } else if (error && error.data) {
         if(error.data?.error){
           toast.error(error.data?.error?.message)
         } else {
           toast.error(error.data?.message)
        }
      }
      fetchTeamMembers && fetchTeamMembers()
    }
    handleRemoveMemberClick(payload,callback)
  }

  const getPermissionIcon = (hasPermission) => {
    return hasPermission
      ? (
        <CheckCircleIcon sx={{ color: theme.palette.primary.main }} fontSize='small' />
        )
      : (
        <CancelOutlinedIcon color='error' fontSize='small' />
        )
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
  }

  useEffect(() => {
    setMembers(teamMembers || [])
  }, [teamMembers])

  return (
    <Box sx={{ width: '100%' }}>
      {members && members.length > 0
        ? (
          <Paper elevation={0} sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
              <Table stickyHeader sx={{ minWidth: 650 }} aria-label='members table'>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Manage Sub Users</TableCell>
                    <TableCell align='center'>Manage Branding</TableCell>
                    <TableCell align='center'>Manage Plans</TableCell>
                    <TableCell align='center'>Manage Team Members</TableCell>
                    <TableCell>Invited By</TableCell>
                    <TableCell>Updated By</TableCell>
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((member) => (
                      <TableRow
                        key={member._id}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                        }}
                      >
                        <TableCell component='th' scope='row'>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.light' }}>
                              {
                              member?.name
                                ? member?.name?.first?.charAt(0).toUpperCase() +
                                member?.name?.last?.charAt(0).toUpperCase()
                                : '?'
}
                            </Avatar>
                            <Typography variant='body2'>
                              {member?.name ? `${member?.name?.first} ${member?.name?.last}` : ' - '}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2' color='text.secondary'>
                            {member.email}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>{getPermissionIcon(member.permissions.manageSubUsers !== 'None')}</TableCell>
                        <TableCell align='center'>
                          {getPermissionIcon(member.permissions.manageBranding)}
                        </TableCell>
                        <TableCell align='center'>
                          {getPermissionIcon(member.permissions.managePlans)}
                        </TableCell>
                        <TableCell align='center'>
                          {getPermissionIcon(member.permissions.manageTeamMembers)}
                        </TableCell>
                        <TableCell align={member.createdBy ? 'left' : 'center'}>
                          {member.createdBy ? member.createdBy : '-'}
                        </TableCell>
                        <TableCell align={member.updatedBy ? 'left' : 'center'}>
                          {member.updatedBy ? member.updatedBy : '-'}
                        </TableCell>
                        <TableCell align='right'>
                          <IconButton aria-label='actions' onClick={(e) => handleMenuOpen(e, member)}>
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component='div'
              count={members.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
            />
          </Paper>
          )
        : (
          <Box marginBlock={2} padding={2} border='1px solid #E4E4E5'>
            <Typography textAlign='center' width='100%' variant='body1'>
              No Members Found
            </Typography>
          </Box>
          )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
            mt: 0.5,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5
            }
          }
        }}
      >
        <MenuItem onClick={handleOpenManagePermissionsDialog}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SettingsIcon
              sx={{ mr: 1.5, color: `${theme.palette.primary.main}` }}
              fontSize='small'
            />
            <Typography variant='body2'>Manage Permissions</Typography>
          </Box>
        </MenuItem>
        <MenuItem onClick={() => currentMember && removeMember(currentMember._id)}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CancelIcon sx={{ mr: 1.5, color: 'error.main' }} fontSize='small' />
            <Typography variant='body2' color='error.main'>
              Remove
            </Typography>
          </Box>
        </MenuItem>
      </Menu>

      {
        openManagePermissionsDialog &&
          <PermissionDialog
            openManagePermissionsDialog={openManagePermissionsDialog}
            handleCloseManagePermissionsDialog={handleCloseManagePermissionsDialog}
            currentMember={currentMember}
            fetchTeamMembers={() => fetchTeamMembers && fetchTeamMembers()}
          />
      }

      {
        openDeleteDialog &&
          <DeleteMemberOrCancelRequest
            open={openDeleteDialog}
            onClose={handleCloseDeleteDialog}
            onDelete={handleConfirmDelete}
          />
      }
    </Box>
  )
}

export default TeamMembersTable
